import { useCallback, useEffect, useState } from 'react'
import { usePublish, useView } from '@10000/react'
import { Box, TooltipSeries } from '../widgets'
import { NextSteps, PracticeBreakdown, useAnalyticsData, PracticeAnalytics, Controls, ResetTooltips } from '.'
import { YourDataDialogs, PracticeBreakdownDialog, GuidanceDialogs } from './guideDialogs'
import { Guidance } from './Guidance.jsx'

export const YourData = ({ displayMode, ...props }) => {
  const { breakdownOpen, setBreakdownOpen, guidanceOpen, setGuidanceOpen, nextStepsOpen, setNextStepsOpen, resultAvailable, setDetailOpen } = useAnalyticsData()
  const { options: { guides } = {}, analyticsGuidesCompleted: { yourData, breakdown, guidance } = {}, presenter } = useView('squiz')
  const publish = usePublish('squiz')
  const [initial, setInitial] = useState(true)

  const [guideActive, setGuideActive] = useState(undefined)

  const completeTutorial = guide => {
    if(presenter) {
      publish('analyticsGuideCompleted', { guide })
    }
    setGuideActive(undefined)
  }

  const handleGuideActivating = useCallback(guide =>
    setGuideActive(presenter ? undefined : guide)
  , [presenter, setGuideActive])

  const resetYourDataGuide = useCallback(() => {
    setDetailOpen(false)
    handleGuideActivating('yourData')
  }, [setDetailOpen, handleGuideActivating])

  useEffect(() => {
    if(initial && guides) {
      if(!yourData && !breakdownOpen && !guidanceOpen) {
        resetYourDataGuide()
      }
      setInitial(false)
    }
  }, [initial, guides, yourData, breakdownOpen, guidanceOpen, resetYourDataGuide])

  return (
    <Box
      column
      fullWidth
      fullHeight
      alignItems="center"
      className="your-data"
      {...props}
    >
      {!resultAvailable
        ? 'See display screen'
        : <>
            <TooltipSeries
              complete={(!guides || yourData) && guideActive !== 'yourData'}
              onComplete={() => completeTutorial('yourData')}
              onReset={resetYourDataGuide}
              active={!breakdownOpen && !guidanceOpen && !displayMode}
            >
              {!displayMode && <Controls />}
              <PracticeAnalytics displayMode={displayMode} />
              {(!breakdownOpen && !guidanceOpen && !displayMode) && <ResetTooltips guide="yourData" />}
              <YourDataDialogs />
            </TooltipSeries>

            <SlidePanel open={breakdownOpen}>
              <TooltipSeries
                complete={(!guides || breakdown) && guideActive !== 'breakdown'}
                onComplete={() => completeTutorial('breakdown')}
                onReset={() => handleGuideActivating('breakdown')}
                active={breakdownOpen && !displayMode}
              >
                <PracticeBreakdown
                  onClose={() => setBreakdownOpen(false)}
                  displayMode={displayMode}
                />
                {(breakdownOpen && !displayMode) && <ResetTooltips guide="breakdown" />}
                <PracticeBreakdownDialog />
              </TooltipSeries>
            </SlidePanel>

            <SlidePanel open={guidanceOpen}>
              <TooltipSeries
                complete={(!guides || guidance) && guideActive !== 'guidance'}
                onComplete={() => completeTutorial('guidance')}
                onReset={() => handleGuideActivating('guidance')}
                active={guidanceOpen && !displayMode}
              >
                <Guidance
                  onClose={() => setGuidanceOpen(false)}
                  displayMode={displayMode}
                />
                {(guidanceOpen && !displayMode) && <ResetTooltips guide="guidance" />}
                <GuidanceDialogs />
              </TooltipSeries>
            </SlidePanel>

            <SlidePanel open={nextStepsOpen}>
              <NextSteps
                onClose={() => setNextStepsOpen(false)}
                displayMode={displayMode}
              />
            </SlidePanel>
        </>
      }
    </Box>
  )
}

const SlidePanel = ({ open, children }) => (
  <Box
    position="absolute"
    top={open ? 60 : '100%'}
    left={0}
    right={0}
    transition={0.5}
    height="calc(100% - 60px)"
    background="black"
    zIndex={open ? 31 : 30}
    overflowY="auto"
  >
    {children}
  </Box>
)